import { Sidebar as HaiSidebar, Icon } from '@hai/ui-react';
import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import MakitoLogoX4 from 'src/images/makito-x4-logo.svg';
import bgMK from 'src/images/sidebar-bg.jpg';
import SmallLogoX4 from 'src/images/small-logo-x4.svg';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { translateRoutes } from 'src/js/helper/sidebar-route-helper';
import { useFullStory, useSendFsAdminStats, useSendFsStats } from 'src/js/hook/use-full-story';
import { useIsSaved } from 'src/js/hook/use-is-saved';
import { useSuccessReaction } from 'src/js/hook/use-reaction';
import { useStores } from 'src/js/hook/use-stores';
import { useTask } from 'src/js/hook/use-task';
import { SidebarRoute } from 'src/js/model/sidebar-route';
import { handleLogout } from 'src/js/pages/login/login';
import { setBrightnessContrast } from 'src/js/pages/settings/status/user-settings';
import { log } from 'src/js/service/logger';
import { useWatch, useWatchObject } from 'src/js/store/use-watch';
import { createLogoutTask } from 'src/js/task/session-task';
import { constant } from 'src/js/constant';
import { infoCenterHelpExists, localHelpExists } from 'src/js/util/online-status-util';
import { useLocalStorage, useReadLocalStorage } from 'usehooks-ts';
import { generateDataAuto } from '../util/automation';
import decoderModels from '../pages/streaming/decoder/decoder-store';
import streamModels from '../pages/streaming/stream/stream-store';
import { watchEvent } from '../events';
import helpStore from '../store/help-store';
import { useMutable } from '../hook/use-mutable';

// Note: If adding new items here also add to translateRoutes()
const sidebarData: (currentPath: string, t: TFunction) => SidebarRoute[] = (currentPath = '', t: TFunction) => {
  return [
    {
      key: 'dashboard',
      iconName: 'Dashboard',
      title: 'sidebar.dashboard',
      to: '/',
      defaultPath: currentPath,
    },
    {
      key: 'streams',
      iconName: 'Streams',
      title: 'sidebar.streams',
      to: '/streaming',
      tabsUseAbsolutePath: true,
      tabData: [
        {
          eventKey: '/streaming/output',
          caption: t('streaming.tabs.outputs'),
        },
        {
          eventKey: '/streaming/decoder',
          caption: t('streaming.tabs.decoder'),
        },
        {
          eventKey: '/streaming/stillImages',
          caption: t('streaming.tabs.stillImages'),
        },
        {
          eventKey: '/streaming/stream',
          caption: t('streaming.tabs.stream'),
        },
      ],
    },
    {
      key: 'settings',
      iconName: 'Settings',
      title: 'sidebar.settings',
      to: '/settings',
      tabsUseAbsolutePath: true,
      tabData: [
        {
          eventKey: '/settings/decoder',
          caption: 'todo',
        },
      ],
    },
    {
      key: 'security',
      iconName: 'Security',
      title: 'sidebar.security',
      to: '/security',
      tabsUseAbsolutePath: true,
      tabData: [
        {
          eventKey: '/security/decoder',
          caption: 'todo',
        },
      ],
    },
    {
      key: 'helpCenter',
      iconName: 'HelpCenter',
      title: 'sidebar.help',
      to: '/help',
    },
  ];
};

export const Sidebar: React.FunctionComponent = () => {
  const logoutTask = useTask(createLogoutTask());
  const { sessionStore } = useStores();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useLocalStorage(
    `${constant.lStorage.sidebarCollapsed}:${sessionStore.username}`,
    false,
  );
  const [isManuallyToggled, setIsManuallyToggled] = useLocalStorage(
    `${constant.lStorage.sidebarToggledManually}:${sessionStore.username}`,
    false,
  ); // if the user specifically toggled the sidebareither way dont annoy him with auto-collapsing/expanding
  const [isManuallyToggledMutable, setIsManuallyToggledMutable] = useMutable(isManuallyToggled); // R4KD-2887, the isManuallyToggled value in callback is sometime stale.
  const brightness = useReadLocalStorage<string>(`${constant.lStorage.brightness}:${sessionStore.username}`) ?? '100';
  const contrast = useReadLocalStorage<string>(`${constant.lStorage.contrast}:${sessionStore.username}`) ?? '100';

  const SIDEBAR_COLLAPSED_BREAKPOINT = 1300;
  useEffect(() => {
    // doc.haivision.com check routine
    infoCenterHelpExists((err) => {
      if (err) {
        localHelpExists((err) => {
          if (!err) {
            helpStore.online = false;
          }
        });
      } else {
        helpStore.online = true;
      }
    });

    const body = document.getElementsByTagName('body');
    setTimeout(() => {
      let newCollapsedState = collapsed;
      if (window.innerWidth < SIDEBAR_COLLAPSED_BREAKPOINT && !collapsed && !isManuallyToggled) {
        // if the window is smaller than 1100px, we always collapse the sidebar
        setCollapsed(true);
        newCollapsedState = true;
      }
      if (body && typeof body[0].classList !== undefined) {
        if (newCollapsedState) {
          body[0].classList.add('sidebar-collapsed');
        } else {
          body[0].classList.remove('sidebar-collapsed');
        }
      }
    }, 1);

    setBrightnessContrast(brightness, contrast);

    const checkViewportSize = () => {
      // console.log('checking viewport size', window.innerWidth, isManuallyToggled);
      if (window.innerWidth >= SIDEBAR_COLLAPSED_BREAKPOINT) {
        // if the window is larger than 1100px, we expand the sidebar
        if (isManuallyToggledMutable() === false) {
          setCollapsed(false);
          if (body && typeof body[0].classList !== undefined) {
            body[0].classList.remove('sidebar-collapsed');
          }
        }
      }
      if (window.innerWidth < SIDEBAR_COLLAPSED_BREAKPOINT) {
        // if the window is smaller than 1100px, we collapse the sidebar

        // console.log('will collapse sidebar due to viewport size');
        if (isManuallyToggledMutable() === false) {
          setCollapsed(true);
          if (body && typeof body[0].classList !== undefined) {
            body[0].classList.add('sidebar-collapsed');
          }
        }
      }
    };

    // Listen for window resize
    window.addEventListener('resize', checkViewportSize);
    return () => {
      window.removeEventListener('resize', checkViewportSize);
    };
  }, []);

  useSuccessReaction(logoutTask, () => {
    handleLogout(false, navigate);
  });

  // signout begins
  const onSignoutProceed = () => {
    logoutTask();
  };

  const onSignoutCancel = () => {
    log.trace('cancelled signout');
  };

  const { check, MyModal } = useIsSaved(onSignoutProceed, onSignoutCancel);
  const signout = () => {
    if (sessionStore.isUser()) {
      logoutTask(); // we dont have permission on presets or isSaved request. No need to check.
      return;
    }
    // check if unsaved preset first
    check();
  };
  // signout ends

  const handleSelect = (path?: string) => {
    if (path != null) {
      navigate(path);
    }
  };

  const handleClickUserName = () => {
    navigate('/security/accounts', { state: { id: sessionStore.username } });
  };

  const toggleCollapse = () => {
    const body = document.getElementsByTagName('body');
    if (body && typeof body[0].classList !== undefined) {
      if (collapsed) {
        body[0].classList.remove('sidebar-collapsed');
      } else {
        body[0].classList.add('sidebar-collapsed');
      }
    }
    setCollapsed(!collapsed);
    setIsManuallyToggled(true); // mark that the sidebar was manually toggled
    setIsManuallyToggledMutable(true); // R4KD-2887
  };

  let defaultActive = location.pathname;
  if (/^\/settings\//.exec(defaultActive)) {
    defaultActive = '/settings';
  } else if (/^\/security\//.exec(defaultActive)) {
    defaultActive = '/security';
  } else if (/^\/dashboard/.exec(defaultActive)) {
    defaultActive = '/';
  } else if (/^\/streaming/.exec(defaultActive)) {
    defaultActive = '/streaming';
  }

  const [systemInfoFetched, setSystemInfoFetched] = useState(systemInfo.version !== '');
  const [analytics, setAnalytics] = useState(systemInfoCapabilities.hasAnalytics());
  useWatchObject(systemInfo, () => setSystemInfoFetched(true));
  useFullStory(analytics, systemInfoFetched);
  useWatch(systemInfo, 'analytics', () => setAnalytics(systemInfoCapabilities.hasAnalytics()));

  // FS analytics for decoders and streams
  const [decFetched, setDecFetched] = useState(false);
  useWatchObject(decoderModels, () => setDecFetched(true));
  const [streamsFetched, setStreamsFetched] = useState(false);
  useWatchObject(streamModels, () => setStreamsFetched(true));
  useSendFsStats(analytics, decFetched, streamsFetched);

  // FS analytics for admin stuff
  const [licensesFetched, setLiensesFetched] = useState(false);
  watchEvent('licenses-fetched', () => {
    setLiensesFetched(true);
  });
  const [certificatesFetched, setCertificatesFetched] = useState(false);
  watchEvent('certificates-fetched', () => {
    setCertificatesFetched(true);
  });

  const [stillImagesCount, setStillImageCount] = useState();
  watchEvent('stillimages-fetched', (data: any) => {
    setStillImageCount(data.count);
  });
  useSendFsAdminStats(analytics, licensesFetched, certificatesFetched, stillImagesCount);

  const sidebarMenu = translateRoutes(sidebarData(location.pathname, t), t);
  return (
    <>
      <HaiSidebar
        logo={<Icon src={MakitoLogoX4} style={{ height: '70px' }} />}
        /*logoSrc={logo} TODO we should also be able to use this but doesn't work at the moment with sizing */
        logoCollapsedSrc={SmallLogoX4}
        accentColor={constant.productColor}
        backgroundImage={bgMK}
        menuContent={sidebarMenu}
        onClickUserName={handleClickUserName}
        defaultActive={defaultActive}
        data-auto={generateDataAuto('sidebar', collapsed ? 'collapsed' : 'expanded')}
        onSelect={handleSelect}
        onSignOut={signout}
        signOutText={t('general.signOut')}
        onCollapse={toggleCollapse}
        collapsed={collapsed}
        deviceSettings={{
          userName: sessionStore.username,
          userRole: sessionStore.getRole(),
          deviceName: systemInfo.deviceName,
          deviceIcon: 'Decoders',
        }}
      />
      <MyModal />
    </>
  );
};
